"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import DarkVeil from "@/components/DarkVeil";
import { VimeoBackgroundSquare } from "@/components/vimeo-background-square";
import { ChevronDown } from "lucide-react";

interface HeroSectionProps {
  onScrollToPricing: () => void;
  onScrollToLeads: () => void;
  showFreeClass?: boolean;
}

export function HeroSection({
  onScrollToPricing,
  onScrollToLeads,
  showFreeClass = true,
}: HeroSectionProps) {
  const t = useTranslations("Home");

  return (
    <section className="relative min-h-screen flex items-center px-4 sm:px-8 py-20">
      {/* Dark Veil Background */}
      <div className="absolute inset-0 pointer-events-none">
        <DarkVeil hueShift={245} />
      </div>
      <div className="max-w-7xl mx-auto w-full relative z-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8 animate-fade-in-up">
            {/* Logo above hero text */}
            <div>
              <Image
                src="/images/logo-aa-bianco-300x.png"
                alt="Adrian & Anita Online"
                width={240}
                height={80}
                className="h-16 sm:h-20 lg:h-24 w-auto filter drop-shadow-lg"
                priority
              />
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.1] text-white font-[family-name:var(--font-big-shoulders)] uppercase">
              {t("hero.title")}
            </h1>
            <p className="text-xl sm:text-xl text-gray-300 leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <button
                onClick={onScrollToPricing}
                className="bg-[#E91E63] hover:bg-[#D81B60] text-white text-lg font-bold px-8 py-4 transition-all duration-300 hover:scale-105"
              >
                {t("hero.cta")}
              </button>
              {showFreeClass && (
                <button
                  onClick={onScrollToLeads}
                  className="border-2 border-white/30 hover:border-white/50 hover:bg-white/10 text-white text-lg font-medium px-8 py-4 transition-all duration-300 hover:scale-105"
                >
                  {t("cta.secondaryButton")}
                </button>
              )}
            </div>
          </div>

          {/* Right Side - Single Video Square (Desktop only) */}
          <div className="relative h-[500px] lg:h-[600px] hidden lg:block">
            <VimeoBackgroundSquare className="w-full h-full" />
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden lg:block">
        <ChevronDown className="h-8 w-8 text-white/50" />
      </div>
    </section>
  );
}
